/**
 * 튜토리얼 TSV 행(generated JSON) → `TutorialStep` 파서 (순수 — Phaser/패키지 런타임 의존 0, 타입만 import).
 *
 * `data/tutorialScenarios.tsv` → `npm run export:data` → `generated/tutorialScenarios.json`(RawTutorialRow[]).
 * 이 파서가 그 행들을 검증·정규화하고 scenarioId별로 그룹·정렬한다.
 * (JSON import는 `JsonTutorialScenarioSource`가 담당 — 파서는 fixture로 단독 테스트 가능하게 분리.)
 */
import type {
  TutorialStep,
  TutorialAdvanceMode,
  TutorialMessagePosition,
  TutorialGesture,
  TutorialRectOffset,
} from "@yjsgames/phaser-tutorial";

/** generated/tutorialScenarios.json 한 행의 원시 형태(csv-json-parser 출력 타입). */
export interface RawTutorialRow {
  scenarioId: string;
  stepOrder: number;
  stepId: string;
  /** 쉼표 구분 영역 id(빈 문자열 = 타겟 없음). */
  targets: string;
  messageKey: string;
  advance: string;
  actionKey: string;
  position: string;
  padding: number;
  unifyTargets: boolean;
  autoAdvanceMs: number;
  /** 타겟별 Rect 보정 — `"id:dx,dy,dw,dh;id2:..."`(빈 문자열 = 보정 없음). */
  offsets?: string;
  /** 포인터 연출(none|tap|drag, 기본 none). */
  gesture?: string;
  /** drag 출발 / tap 대상 타겟 id. */
  gestureFrom?: string;
  /** drag 도착 타겟 id. */
  gestureTo?: string;
  /** 쉼표 구분 허용 인터랙션 키(빈 문자열 = 무제한 = 게이트 없음). */
  allowedActions?: string;
}

const ADVANCE_MODES: ReadonlySet<string> = new Set<TutorialAdvanceMode>([
  "tap",
  "spotlightTap",
  "action",
]);
const POSITIONS: ReadonlySet<string> = new Set<TutorialMessagePosition>([
  "top",
  "middle",
  "bottom",
]);
const GESTURES: ReadonlySet<string> = new Set<TutorialGesture>(["none", "tap", "drag"]);

/** 쉼표 구분 문자열 → 트림된 비빈 토큰 배열. */
export function splitTargets(raw: string): string[] {
  return String(raw ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * 타겟별 Rect 보정 파싱. 형식: `"id:dx,dy,dw,dh;id2:dx,dy,dw,dh"`.
 * 누락/비수치 값은 0. 빈 문자열·잘못된 토큰은 무시(빈 객체 반환).
 */
export function parseOffsets(raw: string | undefined): Record<string, TutorialRectOffset> {
  const out: Record<string, TutorialRectOffset> = {};
  for (const entry of String(raw ?? "").split(";")) {
    const trimmed = entry.trim();
    if (!trimmed) continue;
    const colon = trimmed.indexOf(":");
    if (colon <= 0) continue; // id 없음 또는 ':' 누락
    const id = trimmed.slice(0, colon).trim();
    if (!id) continue;
    const nums = trimmed
      .slice(colon + 1)
      .split(",")
      .map((s) => Number(s.trim()) || 0);
    out[id] = { dx: nums[0] ?? 0, dy: nums[1] ?? 0, dw: nums[2] ?? 0, dh: nums[3] ?? 0 };
  }
  return out;
}

/** 원시 행 배열을 검증·정규화 후 scenarioId별 정렬 그룹으로 변환. 유효하지 않으면 throw. */
export function parseTutorialRows(rows: readonly RawTutorialRow[]): Map<string, TutorialStep[]> {
  const byScenario = new Map<string, TutorialStep[]>();
  for (const row of rows) {
    const step = toStep(row);
    const list = byScenario.get(step.scenarioId);
    if (list) list.push(step);
    else byScenario.set(step.scenarioId, [step]);
  }
  for (const list of byScenario.values()) list.sort((a, b) => a.stepOrder - b.stepOrder);
  return byScenario;
}

function toStep(row: RawTutorialRow): TutorialStep {
  if (!ADVANCE_MODES.has(row.advance)) {
    throw new Error(
      `[tutorial] 스텝 "${row.stepId}"의 advance="${row.advance}"가 유효하지 않음 (tap|spotlightTap|action)`,
    );
  }
  if (!POSITIONS.has(row.position)) {
    throw new Error(
      `[tutorial] 스텝 "${row.stepId}"의 position="${row.position}"가 유효하지 않음 (top|middle|bottom)`,
    );
  }
  const advance = row.advance as TutorialAdvanceMode;
  if (advance === "action" && !row.actionKey) {
    throw new Error(`[tutorial] 스텝 "${row.stepId}"는 advance="action"인데 actionKey가 비어있음`);
  }
  const gestureRaw = row.gesture ?? "";
  const gesture: TutorialGesture = GESTURES.has(gestureRaw)
    ? (gestureRaw as TutorialGesture)
    : "none";
  return {
    scenarioId: row.scenarioId,
    stepOrder: Number(row.stepOrder) || 0,
    stepId: row.stepId,
    targets: splitTargets(row.targets),
    messageKey: row.messageKey ?? "",
    advance,
    actionKey: row.actionKey ?? "",
    position: row.position as TutorialMessagePosition,
    padding: Number(row.padding) || 0,
    unifyTargets: Boolean(row.unifyTargets),
    autoAdvanceMs: Number(row.autoAdvanceMs) || 0,
    offsets: parseOffsets(row.offsets),
    gesture,
    gestureFrom: row.gestureFrom ?? "",
    gestureTo: row.gestureTo ?? "",
    allowedActions: splitTargets(row.allowedActions ?? ""),
  };
}
